import { format, parseISO, isValid } from 'date-fns';
import { useId } from 'react';
import {
  Text,
  COLORS,
  Icon,
  Check,
  Close,
  Pause,
  PhoneOutline,
} from '@summer/ui-components';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@summer/ui-mosaic';

const completedStatus = 'COMPLETED' as const;
const startedStatus = 'STARTED' as const;
const failedStatus = 'FAILED' as const;
const cancelledStatus = 'CANCELLED' as const;
const enrollmentActiveStatus = 'active' as const;

type ActorType = 'mst' | 'borrower';

type SmsFactorChange = {
  id: string;
  changeStartedAt: string;
  changeFinishedAt: string | null;
  changeStatus: string;
  actor: {
    email: string;
    type: ActorType;
  };
};

type SmsFactorEnrollment = {
  enrolledAt: string;
  status: 'pending' | 'active';
};

const Timestamp = ({ isoDate }: { isoDate: string }) => {
  const date = parseISO(isoDate);
  if (!isValid(date)) return 'N/A';
  return format(date, "MMM d, yyyy 'at' h:mm a");
};

const getActorTypeDisplay = (type: ActorType) => {
  return type === 'mst' ? 'MST' : 'Borrower';
};

const getActionText = (status: string) => {
  if (status === completedStatus) {
    return 'Phone Number Updated';
  }
  if (status === startedStatus) {
    return 'Phone Number Update Started';
  }
  if (status === failedStatus) {
    return 'Phone Number Update Failed';
  } else if (status === cancelledStatus) {
    return 'Phone Number Update Cancelled';
  }
};

const iconContainerBaseClasses =
  'flex items-center justify-center w-6 h-6 rounded-lg';

const StatusIcon = ({ status }: { status: string }) => {
  if (status === completedStatus) {
    return (
      <div
        className={iconContainerBaseClasses}
        style={{ backgroundColor: COLORS.lightGreen }}
      >
        <Icon
          SvgComponent={Check}
          width={16}
          height={16}
          stroke={COLORS.darkGreen}
          fill={COLORS.darkGreen}
        />
      </div>
    );
  }
  if (status === startedStatus) {
    return (
      <div
        className={iconContainerBaseClasses}
        style={{ backgroundColor: COLORS.lightYellow }}
      >
        <Icon
          SvgComponent={Pause}
          width={16}
          height={16}
          stroke={COLORS.brownOrange}
          fill={COLORS.lightYellow}
        />
      </div>
    );
  }
  return (
    <div
      className={iconContainerBaseClasses}
      style={{ backgroundColor: COLORS.lightPink }}
    >
      <Icon
        SvgComponent={Close}
        width={8}
        height={8}
        fill={COLORS.magentaDark}
      />
    </div>
  );
};

export type PhoneNumberHistoryBorrower = {
  email?: string;
  smsFactorChanges?: SmsFactorChange[];
  smsFactorEnrollment?: SmsFactorEnrollment | null;
};

type PhoneNumberUpdateHistoryProps = {
  borrower: PhoneNumberHistoryBorrower;
};

const PhoneNumberUpdateHistory = ({
  borrower: { email: borrowerEmail, smsFactorChanges, smsFactorEnrollment },
}: PhoneNumberUpdateHistoryProps) => {
  const baseId = useId();
  const history = smsFactorChanges ?? [];

  if (history.length === 0 && !smsFactorEnrollment) {
    return null;
  }

  const isEnrollmentActive =
    smsFactorEnrollment?.status === enrollmentActiveStatus;

  return (
    <div className="my-6">
      <Accordion type="single" collapsible>
        <AccordionItem value="phone-history">
          <AccordionTrigger>
            <div className="pl-3 flex row justify-between align-center line-height-2 gap-2">
              <div className={iconContainerBaseClasses}>
                <Icon
                  SvgComponent={PhoneOutline}
                  width={16}
                  height={16}
                  fill={COLORS.darkestGrey}
                />
              </div>
              <h2 id={`${baseId}-update-history`}>
                Phone Number Update history
              </h2>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="overflow-x-auto mt-4">
              <table
                className="w-full border-collapse"
                aria-labelledby={`${baseId}-update-history`}
                role="table"
              >
                <thead>
                  <tr className="border-b" style={{ borderColor: COLORS.grey }}>
                    <th
                      className="text-left p-3 font-semibold"
                      style={{ color: COLORS.darkGrey }}
                      scope="col"
                    >
                      Action & Application
                    </th>
                    <th
                      className="text-left p-3 font-semibold"
                      style={{ color: COLORS.darkGrey }}
                      scope="col"
                    >
                      Initiator
                    </th>
                    <th
                      className="text-left p-3 font-semibold"
                      style={{ color: COLORS.darkGrey }}
                      scope="col"
                    >
                      Timestamp
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {history.map(change => (
                    <tr
                      key={change.id}
                      className="border-b"
                      style={{ borderColor: COLORS.grey }}
                    >
                      <td className="p-3 align-top">
                        <div className="flex items-center gap-2">
                          <StatusIcon status={change.changeStatus} />
                          <Text>{getActionText(change.changeStatus)}</Text>
                        </div>
                      </td>
                      <td className="p-3 align-top">
                        <div className="flex flex-col gap-1">
                          <Text>{change.actor.email}</Text>
                          <Text size="small" color="secondary">
                            {getActorTypeDisplay(change.actor.type)}
                          </Text>
                        </div>
                      </td>
                      <td className="p-3 align-top">
                        <Timestamp
                          isoDate={
                            change.changeFinishedAt ?? change.changeStartedAt
                          }
                        />
                      </td>
                    </tr>
                  ))}
                  {smsFactorEnrollment && (
                    <tr className="border-b border-gray-200">
                      <td className="p-3 align-top">
                        <div className="flex items-center gap-2">
                          <StatusIcon
                            status={
                              isEnrollmentActive
                                ? completedStatus
                                : startedStatus
                            }
                          />
                          <span>
                            {isEnrollmentActive
                              ? 'Phone Number Enrolled'
                              : 'Phone Number Enrollment Started'}
                          </span>
                        </div>
                      </td>
                      <td className="p-3 align-top">
                        <div className="flex flex-col gap-1">
                          {borrowerEmail && <span>{borrowerEmail}</span>}
                          <span className="text-sm text-gray-500">
                            {getActorTypeDisplay('borrower')}
                          </span>
                        </div>
                      </td>
                      <td className="p-3 align-top">
                        <Timestamp isoDate={smsFactorEnrollment.enrolledAt} />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

export default PhoneNumberUpdateHistory;
